import React, { useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { RevealText } from "./RevealText";

const ALL_PROJECTS = [
  {
    name: "Fleurish Mjk",
    year: "2025",
    category: "E-commerce",
    img: "https://images.pexels.com/photos/14656012/pexels-photo-14656012.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Expense Tracker Bot",
    year: "2025",
    category: "Automation",
    img: "https://images.pexels.com/photos/4144923/pexels-photo-4144923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "QuantMind AI",
    year: "2024",
    category: "Fintech",
    img: "https://images.pexels.com/photos/30268012/pexels-photo-30268012.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Northbound CRM",
    year: "2024",
    category: "SaaS",
    img: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Drift Atlas",
    year: "2024",
    category: "Internal Tooling",
    img: "https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Bloomly Studio",
    year: "2023",
    category: "Brand Site",
    img: "https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const ProjectsList = () => {
  const [hovered, setHovered] = useState(null);
  const previewRef = useRef(null);

  const updatePreview = (e) => {
    if (!previewRef.current) return;
    const offsetX = 36;
    const offsetY = -120;
    previewRef.current.style.transform = `translate3d(${e.clientX + offsetX}px, ${e.clientY + offsetY}px, 0) rotate(-4deg)`;
  };

  return (
    <section
      id="all-projects"
      data-testid="projects-list-section"
      className="relative py-24 sm:py-32 border-t border-white/5 overflow-hidden"
      onMouseMove={updatePreview}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#B026FF] mb-4">
              Project Index
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
              <RevealText>Every project, indexed.</RevealText>
            </h2>
          </div>
          <p className="text-white/55 max-w-md">
            Hover any title to peek a frame from the live build.
          </p>
        </div>

        <ul className="border-t border-white/10">
          {ALL_PROJECTS.map((p, i) => {
            const isHovered = hovered === i;
            return (
              <li
                key={p.name}
                data-testid={`project-list-item-${i}`}
                data-cursor="hover"
                onMouseEnter={(e) => {
                  setHovered(i);
                  updatePreview(e);
                }}
                onMouseLeave={() => setHovered(null)}
                className="group relative border-b border-white/10 cursor-pointer"
              >
                <div className="grid grid-cols-12 items-baseline py-7 transition-all duration-300">
                  <span className="col-span-2 sm:col-span-1 font-mono-ui text-xs text-white/40 pt-3">
                    0{i + 1}
                  </span>
                  <span
                    className={`col-span-6 sm:col-span-7 font-display text-3xl sm:text-5xl font-semibold transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
                      isHovered
                        ? "text-white translate-x-4"
                        : "text-white/30"
                    } ${
                      hovered !== null && !isHovered ? "blur-[1px]" : ""
                    }`}
                  >
                    {p.name}
                  </span>
                  <span className="hidden sm:block sm:col-span-2 text-xs text-white/45 font-mono-ui uppercase tracking-widest">
                    {p.category}
                  </span>
                  <span className="col-span-3 sm:col-span-1 text-xs text-white/45 text-right font-mono-ui">
                    {p.year}
                  </span>
                  <span className="col-span-1 flex justify-end text-white/40 group-hover:text-[#B026FF] transition-colors">
                    <ArrowUpRight size={20} />
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Floating preview that follows the cursor */}
      <div
        ref={previewRef}
        aria-hidden="true"
        data-testid="projects-list-preview"
        className={`pointer-events-none fixed top-0 left-0 z-30 transition-opacity duration-300 ease-out ${
          hovered !== null ? "opacity-100" : "opacity-0"
        }`}
        style={{ willChange: "transform" }}
      >
        <div className="relative w-72 h-44 rounded-xl overflow-hidden border border-[#B026FF]/40 shadow-[0_30px_70px_-20px_rgba(176,38,255,0.6)] bg-black">
          {hovered !== null && (
            <img
              key={hovered}
              src={ALL_PROJECTS[hovered].img}
              alt={ALL_PROJECTS[hovered].name}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 flex justify-between font-mono-ui text-[10px] text-white/85 uppercase tracking-widest">
            <span>{hovered !== null ? ALL_PROJECTS[hovered].category : ""}</span>
            <span>{hovered !== null ? ALL_PROJECTS[hovered].year : ""}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
